import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, GridOptions, ApplyColumnStateParams } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { TaxFeedLogListAgGridDataSource } from './agGridDataSource';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes';
import { Subscription } from 'rxjs';
import { TaxFeedLogService } from '../taxFeedLog.service';
import { TaxFeedLogRepository } from '../taxFeedLog.repository';
import { TaxFeedLogListGridActionCellRendererComponent, TaxFeedLogListGridActionCellRendererParams } from './agGridActionCellRenderer.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { TaxFeedLogDetailsModalComponent, TaxFeedLogDetailsParams } from '../Tax-Feed-Log-Details/taxFeedLogDetailsModal.component';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'tax-feed-log-list',
    templateUrl: './taxFeedLogList.component.html',
    styleUrls: ['./taxFeedLogList.component.scss'],
    standalone: false
})
export class TaxFeedLogListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _taxFeedLogRepository: TaxFeedLogRepository,
        private readonly _taxFeedLogService: TaxFeedLogService,
        private readonly _modalService: WeissmanModalService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler) {
    }

    gridId: System.Guid = '853BA97B-9F10-4224-A8EA-6B6563F7AA4D';
    isInitialized: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);
    gridApi: GridApi;

    private _gridDataSource: TaxFeedLogListAgGridDataSource;
    private _refreshSub: Subscription;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    ngOnInit(): void {
        if (!(this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW) || this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT))) {
            this.navigateToUnauthorized();
            return;
        }

        this._refreshSub = this._taxFeedLogService.refresh$.subscribe(() => {
            this._refreshDataSource();
        });

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._refreshSub && this._refreshSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Summary ID',
                field: 'taxFeedSummaryLogId',
                width: AgGridColumns.textColumnSmallWidth,
                maxWidth: AgGridColumns.textColumnMedWidth
            },
            {
                headerName: 'Operaton Type',
                field: 'operationType',
                width: AgGridColumns.textColumnMedWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Start Time',
                field: 'startTime',
                width: AgGridColumns.dateColumnWidth,
                maxWidth: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxFeedSummaryLogModel;
                    if (!(model && model.startTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.startTime);
                }
            },
            {
                headerName: 'End Time',
                field: 'endTime',
                width: AgGridColumns.dateColumnWidth,
                maxWidth: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxFeedSummaryLogModel;
                    if (!(model && model.endTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.endTime);
                }
            },
            {
                headerName: 'Request Payload',
                field: 'requestPayloadJson',
                width: AgGridColumns.textColumnWidth,
                //maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: 'Response Payload',
                field: 'responsePayloadJson',
                width: AgGridColumns.textColumnWidth,
                //maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: 'Summary Ids',
                field: 'summaryIdsJson',
                width: AgGridColumns.textColumnWidth,
                //maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: TaxFeedLogListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewDetails.bind(this)
                } as TaxFeedLogListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'taxFeedSummaryLogId', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('paginationPageSize', 100);
        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setDataSource();
    }

    async refresh(): Promise<void> {
        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            await this._taxFeedLogService.refresh();
        } finally {
            busyRef.hide();
        }
    }

    navigateToUnauthorized(): void {
        this._routerService.go('unauthorizedAccess', {});
    }

    private async _viewDetails(params: TaxFeedLogListGridActionCellRendererParams): Promise<void> {
        const taxFeedSummaryLog = params.data as Core.TaxFeedSummaryLogModel;
        if (!taxFeedSummaryLog) {
            return;
        }

        const detailParams: TaxFeedLogDetailsParams = {
            summaryLogId: taxFeedSummaryLog.taxFeedSummaryLogId
        };

        await this._modalService.showAsync(TaxFeedLogDetailsModalComponent, detailParams, 'modal-lg');
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }

        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new TaxFeedLogListAgGridDataSource(this.gridApi, this._taxFeedLogRepository);
        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }
}
